import { EditorContent, type Editor } from '@tiptap/react'
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Underline as UnderlineIcon,
  Unlink,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface RichTextEditorProps {
  editor: Editor | null
  disabled?: boolean
  placeholder?: string
  minHeight?: string
  className?: string
  showMentionButton?: boolean
  /** Additive — adds an Underline toggle button. Requires the `underline` StarterKit extension (enabled by default). */
  showUnderlineButton?: boolean
  /** Shows a link-insert button in the toolbar. Requires `useRichTextEditor({ links: true })`. */
  showLinkButton?: boolean
  footer?: ReactNode
}

/** Adds an `https://` scheme when the user typed a bare domain. */
function normalizeLinkHref(rawUrl: string): string {
  return /^[a-z][a-z0-9+.-]*:/i.test(rawUrl) ? rawUrl : `https://${rawUrl}`
}

function LinkToolbarButton({ editor, disabled }: { editor: Editor | null; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const isActive = editor?.isActive('link') ?? false

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setUrl((editor?.getAttributes('link').href as string | undefined) ?? '')
    }
    setOpen(nextOpen)
  }

  const applyLink = () => {
    if (!editor) return
    const trimmed = url.trim()
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalizeLinkHref(trimmed) }).run()
    }
    setOpen(false)
  }

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run()
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || !editor}
          className={isActive ? 'bg-muted' : ''}
          tabIndex={-1}
          aria-label="Insert link"
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <div className="flex items-center gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyLink()
              }
            }}
          />
          <Button type="button" size="sm" onClick={applyLink}>
            Apply
          </Button>
          {isActive && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={removeLink}
              aria-label="Remove link"
            >
              <Unlink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * RichTextEditor - Reusable TipTap editor with formatting toolbar
 *
 * Features:
 * - Toolbar with Bold, Italic, Bullet List, Ordered List
 * - Optional Underline and Link buttons (opt-in via props)
 * - Customizable min height
 * - Disabled state support
 * - Inline CSS for proper list rendering
 */
export function RichTextEditor({
  editor,
  disabled = false,
  minHeight = '120px',
  className,
  showMentionButton = false,
  showUnderlineButton = false,
  showLinkButton = false,
  footer,
}: RichTextEditorProps) {
  return (
    <div className={cn('grid', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 border rounded-t-md p-1 bg-muted/50">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          disabled={disabled || !editor}
          className={editor?.isActive('bold') ? 'bg-muted' : ''}
          tabIndex={-1}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          disabled={disabled || !editor}
          className={editor?.isActive('italic') ? 'bg-muted' : ''}
          tabIndex={-1}
        >
          <Italic className="h-4 w-4" />
        </Button>
        {showUnderlineButton && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            disabled={disabled || !editor}
            className={editor?.isActive('underline') ? 'bg-muted' : ''}
            tabIndex={-1}
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
        )}
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (editor) {
              editor.chain().focus().toggleBulletList().run()
            }
          }}
          disabled={disabled || !editor}
          className={editor?.isActive('bulletList') ? 'bg-muted' : ''}
          tabIndex={-1}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (editor) {
              editor.chain().focus().toggleOrderedList().run()
            }
          }}
          disabled={disabled || !editor}
          className={editor?.isActive('orderedList') ? 'bg-muted' : ''}
          tabIndex={-1}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        {showLinkButton && (
          <>
            <div className="w-px h-6 bg-border mx-1" />
            <LinkToolbarButton editor={editor} disabled={disabled} />
          </>
        )}
        {showMentionButton && (
          <>
            <div className="w-px h-6 bg-border mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                if (editor) {
                  editor
                    .chain()
                    .focus()
                    .command(({ tr }) => {
                      tr.insertText('@')
                      return true
                    })
                    .run()
                }
              }}
              disabled={disabled || !editor}
              tabIndex={-1}
              title="Mention someone"
            >
              <span className="text-sm font-medium">@</span>
            </Button>
          </>
        )}
      </div>

      {/* Editor */}
      <div
        className={cn(
          'flex flex-col border rounded-b-md border-t-0 bg-background',
          !disabled && 'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        style={{ minHeight }}
      >
        <style>{`
          .ProseMirror ul {
            list-style-type: disc;
            margin-left: 1.5rem;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .ProseMirror ol {
            list-style-type: decimal;
            margin-left: 1.5rem;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .ProseMirror li {
            margin-top: 0.25rem;
            margin-bottom: 0.25rem;
          }
          .ProseMirror p.is-editor-empty:first-child::before {
            color: var(--muted-foreground);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
          .ProseMirror .mention {
            color: hsl(var(--primary));
            font-weight: 500;
            background: hsl(var(--primary) / 0.1);
            border-radius: 0.25rem;
            padding: 0.125rem 0.25rem;
          }
        `}</style>
        <EditorContent className="flex-1 focus:outline-none text-sm" editor={editor} />
        {footer}
      </div>
    </div>
  )
}
